import Konva from 'konva';
import type { KonvaEventObject } from 'konva/lib/Node';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Image, Layer, Rect, Stage, Text, Transformer } from 'react-konva';
import useImage from 'use-image';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../constants';
import { useCanvasStore } from '../store/canvasStore';
import type { CanvasStageProps, KonvaTextType, SelectionRectState, TextItem } from '../types';
import { limitDrag } from '../utils/canvas';

const calculateTextHeight = (textItem: TextItem): number => {
  const tempNode = new Konva.Text({
    text: textItem.text || ' ',
    fontSize: textItem.fontSize,
    fontFamily: textItem.fontFamily,
    fontWeight: textItem.fontWeight,
    width: textItem.width,
    align: textItem.align,
    lineHeight: textItem.lineHeight,
    letterSpacing: textItem.letterSpacing,
    padding: 0,
  });
  const height = tempNode.height();
  tempNode.destroy();
  return height;
};

const CanvasStage: React.FC<CanvasStageProps> = () => {
  const stageRef = useCanvasStore((s) => s.stageRef);
  const logoRef = useCanvasStore((s) => s.logoRef);
  const trRef = useCanvasStore((s) => s.trRef);
  const textRefs = useCanvasStore((s) => s.textRefs);
  const bgImage = useCanvasStore((s) => s.bgImage);
  const logoImage = useCanvasStore((s) => s.logoImage);
  const backgroundColor = useCanvasStore((s) => s.backgroundColor);
  const texts = useCanvasStore((s) => s.texts);
  const setTexts = useCanvasStore((s) => s.setTexts);
  const logoProps = useCanvasStore((s) => s.logoProps);
  const setLogoProps = useCanvasStore((s) => s.setLogoProps);
  const selectedId = useCanvasStore((s) => s.selectedId);
  const setSelectedId = useCanvasStore((s) => s.setSelectedId);
  const isEditingTextId = useCanvasStore((s) => s.isEditingTextId);
  const previewMode = useCanvasStore((s) => s.previewMode);
  const handleTextTransform = useCanvasStore((s) => s.handleTextTransform);
  const handleLogoDoubleClick = useCanvasStore((s) => s.handleLogoDoubleClick);
  const handleTextDoubleClick = useCanvasStore((s) => s.handleTextDoubleClick);
  const saveStateToHistory = useCanvasStore((s) => s.saveStateToHistory);
  const updateImage = useCanvasStore((s) => s.updateImage);
  const canvasImages = useCanvasStore((s) => s.canvasImages);
  const updateElementOrder = useCanvasStore((s) => s.updateElementOrder);

  const [selectionRect, setSelectionRect] = useState<SelectionRectState>({
    startX: 0,
    startY: 0,
    width: 0,
    height: 0,
    visible: false,
  });
  const [isSelecting, setIsSelecting] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const isPerformingRectSelect = useRef(false);

  const [bg] = useImage(bgImage ?? '', 'anonymous');
  const [logo] = useImage(logoImage ?? '', 'anonymous');
  const [imageMap, setImageMap] = useState<Record<string, HTMLImageElement | null>>({});
  const bgImageRef = useRef<Konva.Image | null>(null);
  const imageRefs = useRef(new Map());

  useEffect(() => {
    if (!canvasImages.length) return;

    const loadImages = async () => {
      const newImageMap: Record<string, HTMLImageElement | null> = {};

      const loadPromises = canvasImages.map(
        (image) =>
          new Promise<void>((resolve) => {
            const img = document.createElement('img');
            img.crossOrigin = 'anonymous';
            img.onload = () => {
              newImageMap[image.url] = img;
              resolve();
            };
            img.onerror = () => {
              newImageMap[image.url] = null;
              resolve();
            };
            img.src = image.url;
          })
      );

      await Promise.all(loadPromises);
      setImageMap(newImageMap);
    };

    loadImages();
  }, [canvasImages]);

  const handleDragStart = useCallback(() => {
    saveStateToHistory();
  }, [saveStateToHistory]);

  const handleLogoClick = useCallback(() => {
    if (!previewMode) {
      setSelectedId('logo');
    }
  }, [previewMode, setSelectedId]);

  const handleLogoDragEnd = useCallback(
    (e: KonvaEventObject<DragEvent>) => {
      setLogoProps((prev) => ({ ...prev, x: e.target.x(), y: e.target.y() }));
    },
    [setLogoProps]
  );

  const handleDirectLogoTransform = useCallback(
    (e: KonvaEventObject<Event>) => {
      saveStateToHistory();
      if (!logoRef.current) return;

      const konvaLogo = logoRef.current as unknown as Konva.Image;
      const node = e.target;
      const scaleX = node.scaleX();
      const scaleY = node.scaleY();
      const width = node.width() * scaleX;
      const height = node.height() * scaleY;
      const x = node.x();
      const y = node.y();

      konvaLogo.scaleX(1);
      konvaLogo.scaleY(1);
      konvaLogo.width(width);
      konvaLogo.height(height);

      setLogoProps((prev) => ({
        ...prev,
        width,
        height,
        x,
        y,
      }));

      const layer = konvaLogo.getLayer();
      if (layer) layer.batchDraw();
    },
    [logoRef, setLogoProps, saveStateToHistory]
  );

  const updateTransformerForMultiSelection = useCallback(() => {
    if (trRef.current && stageRef.current && Array.isArray(selectedId) && selectedId.length > 1) {
      const transformer = trRef.current as unknown as Konva.Transformer;
      const nodes: Konva.Node[] = [];

      if (stageRef.current) {
        const allNodes = stageRef.current.find('Text, Image');
        allNodes.forEach((node) => {
          if (node.id() !== 'logo' && !node.id().includes('background')) {
            node.setAttr('stroke', undefined);
            node.setAttr('strokeWidth', 0);
            node.setAttr('className', '');
          }
        });
      }

      selectedId.forEach((id) => {
        const textNode = stageRef.current?.findOne(`#text-${id}`);
        if (textNode) {
          nodes.push(textNode as Konva.Node);
          textNode.setAttr('stroke', '#3498db');
          textNode.setAttr('strokeWidth', 1);
          textNode.setAttr('className', 'multi-selected');
        }

        const imgNode = stageRef.current?.findOne(`#${id}`);
        if (imgNode) {
          nodes.push(imgNode as Konva.Node);
          imgNode.setAttr('stroke', '#3498db');
          imgNode.setAttr('strokeWidth', 1);
          imgNode.setAttr('className', 'multi-selected');
        }
      });

      if (nodes.length > 0) {
        transformer.nodes(nodes);
        transformer.getLayer()?.batchDraw();

        nodes.forEach((node) => {
          node.draggable(true);

          node.on('dragmove', () => {
            transformer.getLayer()?.batchDraw();
          });
        });
      }
    }
  }, [selectedId, trRef, stageRef]);

  useEffect(() => {
    if (stageRef.current) {
      const allNodes = stageRef.current.find('Text, Image');
      allNodes.forEach((node) => {
        if (node.id() !== 'logo' && !node.id().includes('background')) {
          node.setAttr('stroke', undefined);
          node.setAttr('strokeWidth', 0);
          node.setAttr('className', '');
        }
      });
    }

    if (Array.isArray(selectedId) && selectedId.length > 1) {
      setTimeout(updateTransformerForMultiSelection, 10);
    }
  }, [selectedId, updateTransformerForMultiSelection, stageRef]);

  const renderTexts = useMemo(() => {
    return texts.map((textItem) => {
      const {
        id,
        text,
        x,
        y,
        width,
        color,
        align,
        fontFamily,
        fontSize,
        fontWeight,
        lineHeight,
        letterSpacing,
        italic,
      } = textItem;
      const exactHeight = calculateTextHeight(textItem);
      const isSelected = selectedId === id || (Array.isArray(selectedId) && selectedId.includes(id));

      return (
        <Text
          key={id}
          id={`text-${id}`}
          data-id={id}
          className={isSelected ? 'selected' : ''}
          ref={(node) => {
            if (node) {
              textRefs.current?.set(id, node as unknown as KonvaTextType);
              node.height(exactHeight);
              node.padding(0);

              if (italic) {
                node.fontStyle(fontWeight === 'bold' ? 'bold italic' : 'italic');
              } else if (fontWeight === 'bold') {
                node.fontStyle('bold');
              } else {
                node.fontStyle('normal');
              }
            } else if (textRefs.current) {
              textRefs.current.delete(id);
            }
          }}
          text={text}
          fontSize={fontSize}
          fontWeight={fontWeight}
          fill={color}
          x={x}
          y={y}
          width={width}
          height={exactHeight}
          align={align}
          fontFamily={fontFamily}
          lineHeight={lineHeight}
          letterSpacing={letterSpacing}
          padding={0}
          draggable={!previewMode}
          onClick={(e) => {
            if (!previewMode) {
              e.cancelBubble = true;
              setSelectedId(id);
            }
          }}
          onDblClick={(e) => {
            if (!previewMode) {
              e.cancelBubble = true;

              if (Array.isArray(selectedId) && selectedId.length > 1) {
                setSelectedId(id);

                if (stageRef.current) {
                  const allNodes = stageRef.current.find('Text, Image');
                  allNodes.forEach((node) => {
                    if (node.id() !== 'logo' && !node.id().includes('background')) {
                      node.setAttr('stroke', undefined);
                      node.setAttr('strokeWidth', 0);
                      node.setAttr('className', '');
                    }
                  });

                  const existingDragArea = stageRef.current.findOne('#multi-select-drag-area');
                  if (existingDragArea) {
                    existingDragArea.destroy();
                  }
                }
              }

              handleTextDoubleClick(id);
            }
          }}
          dragBoundFunc={(pos) => limitDrag(pos, width, fontSize * 1.5)}
          onDragStart={handleDragStart}
          onDragMove={(e) => {
            if (Array.isArray(selectedId) && selectedId.length > 1 && trRef.current) {
              trRef.current.getLayer()?.batchDraw();
            }
          }}
          onDragEnd={(e) => {
            setTexts((prev) => prev.map((t) => (t.id === id ? { ...t, x: e.target.x(), y: e.target.y() } : t)));
            if (Array.isArray(selectedId) && selectedId.length > 1) {
              setTimeout(updateTransformerForMultiSelection, 10);
            }
          }}
          onTransformEnd={() => {
            handleTextTransform(id);
            if (Array.isArray(selectedId) && selectedId.length > 1) {
              setTimeout(updateTransformerForMultiSelection, 10);
            }
          }}
          opacity={isEditingTextId === id ? 0 : 1}
        />
      );
    });
  }, [
    texts,
    previewMode,
    setTexts,
    setSelectedId,
    textRefs,
    isEditingTextId,
    handleTextDoubleClick,
    handleTextTransform,
    handleDragStart,
    selectedId,
    updateTransformerForMultiSelection,
  ]);

  const renderImages = useMemo(() => {
    return canvasImages
      .filter((image) => !image.deleted)
      .map((image) => {
        const img = imageMap[image.url];
        const isSelected = selectedId === image.id || (Array.isArray(selectedId) && selectedId.includes(image.id));

        return (
          <Image
            key={image.id}
            id={image.id}
            data-id={image.id}
            className={isSelected ? 'selected' : ''}
            ref={(node) => {
              if (node) {
                imageRefs.current.set(image.id, node);
                if (stageRef.current) {
                  const layer = node.getLayer();
                  if (layer) layer.batchDraw();
                }
              }
            }}
            image={img || undefined}
            x={image.x}
            y={image.y}
            width={image.width}
            height={image.height}
            opacity={image.opacity || 1}
            rotation={image.rotation || 0}
            draggable={!previewMode}
            onClick={(e) => {
              if (!previewMode) {
                e.cancelBubble = true;
                setSelectedId(image.id);
              }
            }}
            dragBoundFunc={(pos) => limitDrag(pos, image.width, image.height)}
            onDragStart={handleDragStart}
            onDragMove={(e) => {
              if (Array.isArray(selectedId) && selectedId.length > 1 && trRef.current) {
                trRef.current.getLayer()?.batchDraw();
              }
            }}
            onDragEnd={(e) => {
              saveStateToHistory();
              updateImage(image.id, { x: e.target.x(), y: e.target.y() });
              if (Array.isArray(selectedId) && selectedId.length > 1) {
                setTimeout(updateTransformerForMultiSelection, 10);
              }
            }}
            onTransformEnd={(e) => {
              const node = e.target;
              const scaleX = node.scaleX();
              const scaleY = node.scaleY();
              const rotation = node.rotation();

              node.scaleX(1);
              node.scaleY(1);

              saveStateToHistory();
              updateImage(image.id, {
                x: node.x(),
                y: node.y(),
                width: Math.round(node.width() * scaleX),
                height: Math.round(node.height() * scaleY),
                rotation: rotation,
              });

              if (Array.isArray(selectedId) && selectedId.length > 1) {
                setTimeout(updateTransformerForMultiSelection, 10);
              }
            }}
          />
        );
      });
  }, [
    canvasImages,
    imageMap,
    selectedId,
    previewMode,
    handleDragStart,
    saveStateToHistory,
    updateImage,
    setSelectedId,
    updateTransformerForMultiSelection,
    stageRef,
  ]);

  const performLiveSelection = useCallback(
    (startX: number, startY: number, width: number, height: number) => {
      const x = width >= 0 ? startX : startX + width;
      const y = height >= 0 ? startY : startY + height;
      const absWidth = Math.abs(width);
      const absHeight = Math.abs(height);

      if (absWidth < 5 && absHeight < 5) return;

      if (!isShiftPressed) {
        setSelectedId(null);
      }

      const selectedElements: string[] = [];

      texts.forEach((text) => {
        if (
          x <= text.x + text.width &&
          x + absWidth >= text.x &&
          y <= text.y + text.fontSize * (text.lineHeight || 1.2) &&
          y + absHeight >= text.y
        ) {
          selectedElements.push(text.id);
        }
      });

      canvasImages.forEach((img) => {
        if (
          !img.deleted &&
          x <= img.x + img.width &&
          x + absWidth >= img.x &&
          y <= img.y + img.height &&
          y + absHeight >= img.y
        ) {
          selectedElements.push(img.id);
        }
      });

      if (selectedElements.length > 0) {
        if (isShiftPressed) {
          const currentSelection = Array.isArray(selectedId)
            ? [...selectedId]
            : selectedId && selectedId !== 'canvas'
              ? [selectedId]
              : [];

          const newSelection = [...new Set([...currentSelection, ...selectedElements])];

          if (newSelection.length === 1) {
            setSelectedId(newSelection[0]);
          } else {
            setSelectedId(newSelection);
            setTimeout(updateTransformerForMultiSelection, 10);
          }
        } else {
          if (selectedElements.length === 1) {
            setSelectedId(selectedElements[0]);
          } else {
            setSelectedId(selectedElements);
            setTimeout(updateTransformerForMultiSelection, 10);
          }
        }
      }
    },
    [texts, canvasImages, selectedId, isShiftPressed, setSelectedId, updateTransformerForMultiSelection]
  );

  const handleMouseDown = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      const clickedOnEmpty =
        e.target === e.target.getStage() ||
        e.target.name() === 'background-rect' ||
        e.target.id() === 'canvas-background-image';

      if (!clickedOnEmpty || previewMode) return;

      if (stageRef.current) {
        const allNodes = stageRef.current.find('Text, Image');
        allNodes.forEach((node) => {
          if (node.id() !== 'logo' && !node.id().includes('background')) {
            node.setAttr('stroke', undefined);
            node.setAttr('strokeWidth', 0);
            node.setAttr('className', '');
          }
        });

        if (trRef.current) {
          const transformer = trRef.current as unknown as Konva.Transformer;
          transformer.nodes([]);
          transformer.getLayer()?.batchDraw();
        }
      }

      if (!isShiftPressed) {
        setSelectedId(null);
      }

      const stage = e.target.getStage();
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      setSelectionRect({
        startX: pos.x,
        startY: pos.y,
        width: 0,
        height: 0,
        visible: true,
      });

      setIsSelecting(true);
      document.body.style.cursor = 'crosshair';

      e.cancelBubble = true;
    },
    [previewMode, stageRef, trRef, isShiftPressed, setSelectedId]
  );

  const handleMouseMove = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      if (!isSelecting) return;

      const stage = e.target.getStage();
      if (!stage) return;

      const pos = stage.getPointerPosition();
      if (!pos) return;

      e.cancelBubble = true;

      setSelectionRect((prev) => {
        const newRect = {
          ...prev,
          width: pos.x - prev.startX,
          height: pos.y - prev.startY,
          visible: true,
        };

        if (Math.abs(newRect.width) > 5 || Math.abs(newRect.height) > 5) {
          performLiveSelection(prev.startX, prev.startY, newRect.width, newRect.height);
        }

        return newRect;
      });
    },
    [isSelecting, performLiveSelection]
  );

  const handleMouseUp = useCallback(() => {
    if (!isSelecting) return;

    setSelectionRect((prev) => ({ ...prev, visible: false }));
    setIsSelecting(false);
    document.body.style.cursor = 'default';

    if (Array.isArray(selectedId) && selectedId.length > 1) {
      setTimeout(updateTransformerForMultiSelection, 10);
    }

    isPerformingRectSelect.current = true;
  }, [isSelecting, selectedId, updateTransformerForMultiSelection]);

  const handleStageClick = useCallback(
    (e: KonvaEventObject<MouseEvent>) => {
      if (isPerformingRectSelect.current) {
        isPerformingRectSelect.current = false;
        return;
      }

      const clickedOnEmpty =
        e.target === e.target.getStage() ||
        e.target.name() === 'background-rect' ||
        e.target.id() === 'canvas-background-image';

      if (clickedOnEmpty) {
        if (stageRef.current) {
          const allNodes = stageRef.current.find('Text, Image');
          allNodes.forEach((node) => {
            if (node.id() !== 'logo' && !node.id().includes('background')) {
              node.setAttr('stroke', undefined);
              node.setAttr('strokeWidth', 0);
              node.setAttr('className', '');
            }
          });

          if (trRef.current) {
            const transformer = trRef.current as unknown as Konva.Transformer;
            transformer.nodes([]);
            transformer.getLayer()?.batchDraw();
          }
        }

        setSelectedId('canvas');
        return;
      }

      if (e.target.getParent()?.className === 'Transformer') return;

      const id = e.target.id();
      if (!id) return;

      const cleanId = id.startsWith('text-') ? id.replace('text-', '') : id;

      if (e.evt.shiftKey) {
        const currentSelection = Array.isArray(selectedId)
          ? [...selectedId]
          : selectedId && selectedId !== 'canvas'
            ? [selectedId]
            : [];

        const idIndex = currentSelection.indexOf(cleanId);
        if (idIndex >= 0) {
          currentSelection.splice(idIndex, 1);
        } else {
          currentSelection.push(cleanId);
        }

        if (currentSelection.length === 0) {
          setSelectedId('canvas');
        } else if (currentSelection.length === 1) {
          setSelectedId(currentSelection[0]);
        } else {
          setSelectedId(currentSelection);
        }
      } else {
        setSelectedId(cleanId);
      }
    },
    [selectedId, setSelectedId, stageRef, trRef]
  );

  useEffect(() => {
    if (!logo) return;

    const hasValidDimensions =
      logoProps.width > 10 &&
      logoProps.height > 10 &&
      logoProps.width < CANVAS_WIDTH &&
      logoProps.height < CANVAS_HEIGHT;

    if (hasValidDimensions) return;

    const maxInitialWidth = CANVAS_WIDTH / 4;
    const aspectRatio = logo.height / logo.width;

    let initialWidth = Math.min(logo.width, maxInitialWidth);
    let initialHeight = initialWidth * aspectRatio;

    if (initialHeight > CANVAS_HEIGHT / 3) {
      initialHeight = CANVAS_HEIGHT / 3;
      initialWidth = initialHeight / aspectRatio;
    }

    setLogoProps((prev) => ({
      ...prev,
      width: initialWidth,
      height: initialHeight,
    }));
  }, [logo, logoProps, setLogoProps]);

  useEffect(() => {
    if (!trRef.current || !stageRef.current) return;

    const transformer = trRef.current as unknown as Konva.Transformer;
    transformer.nodes([]);

    if (Array.isArray(selectedId) && selectedId.length > 0) {
      const nodes: Konva.Node[] = [];

      selectedId.forEach((id) => {
        const textNode = stageRef.current?.findOne(`#text-${id}`);
        if (textNode) {
          nodes.push(textNode as Konva.Node);
          return;
        }

        const imgNode = stageRef.current?.findOne(`#${id}`);
        if (imgNode) {
          nodes.push(imgNode as Konva.Node);
        }
      });

      if (nodes.length > 0) {
        transformer.nodes(nodes);
        transformer.getLayer()?.batchDraw();
      }
    } else if (selectedId === 'logo' && logoRef.current && logo) {
      transformer.nodes([logoRef.current as unknown as Konva.Node]);
      transformer.keepRatio(true);
      transformer.enabledAnchors([
        'top-left',
        'top-center',
        'top-right',
        'middle-left',
        'middle-right',
        'bottom-left',
        'bottom-center',
        'bottom-right',
      ]);
    } else if (selectedId && selectedId !== 'logo' && selectedId !== 'canvas' && !isEditingTextId) {
      const textNode = stageRef.current.findOne(`#text-${selectedId}`);
      if (textNode) {
        transformer.nodes([textNode as Konva.Node]);
        transformer.keepRatio(false);
        transformer.rotateEnabled(false);
        transformer.enabledAnchors([
          'top-left',
          'top-center',
          'top-right',
          'middle-left',
          'middle-right',
          'bottom-left',
          'bottom-center',
          'bottom-right',
        ]);
      } else {
        const imageNode = stageRef.current.findOne(`#${selectedId}`);
        if (imageNode) {
          transformer.nodes([imageNode as Konva.Node]);
          transformer.keepRatio(false);
          transformer.rotateEnabled(true);
          transformer.enabledAnchors([
            'top-left',
            'top-center',
            'top-right',
            'middle-left',
            'middle-right',
            'bottom-left',
            'bottom-center',
            'bottom-right',
          ]);

          transformer.anchorSize(9);
          transformer.anchorCornerRadius(4);
          transformer.anchorStrokeWidth(2);

          transformer.rotationSnaps([0, 45, 90, 135, 180, 225, 270, 315]);
          transformer.rotateAnchorOffset(30);
        }
      }
    }

    if (
      !selectedId ||
      selectedId === 'logo' ||
      selectedId === 'canvas' ||
      stageRef.current.findOne(`#text-${selectedId}`)
    ) {
      transformer.rotateEnabled(false);
    }

    transformer.padding(1);
    transformer.borderStroke('#3498db');
    transformer.borderStrokeWidth(2);
    transformer.anchorStroke('#3498db');
    transformer.anchorFill('#fff');
    transformer.anchorSize(8);

    const layer = transformer.getLayer();
    if (layer) layer.batchDraw();
  }, [selectedId, isEditingTextId, logo, logoRef, trRef, stageRef, texts]);

  useEffect(() => {
    updateElementOrder();
  }, [texts, canvasImages, logoImage, updateElementOrder]);

  useEffect(() => {
    if (selectedId) {
      setTimeout(() => {
        updateElementOrder();
      }, 50);
    }
  }, [selectedId, updateElementOrder]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(true);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftPressed(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Memoize the selection rectangle calculations
  const selectionRectProps = useMemo(() => {
    if (!selectionRect.visible) return null;

    const x = selectionRect.width >= 0 ? selectionRect.startX : selectionRect.startX + selectionRect.width;
    const y = selectionRect.height >= 0 ? selectionRect.startY : selectionRect.startY + selectionRect.height;
    const width = Math.abs(selectionRect.width);
    const height = Math.abs(selectionRect.height);

    return { x, y, width, height };
  }, [selectionRect]);

  return (
    <Stage
      width={CANVAS_WIDTH}
      height={CANVAS_HEIGHT}
      ref={stageRef}
      onClick={handleStageClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={{
        border: '1px solid #e2e8f0',
        borderRadius: '4px',
        maxWidth: '100%',
        position: 'relative',
      }}
    >
      <Layer>
        <Rect
          x={0}
          y={0}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          fill={backgroundColor}
          name="background-rect"
          listening={true}
          onClick={(e: KonvaEventObject<MouseEvent>) => {
            e.cancelBubble = true;
            setSelectedId('canvas');
          }}
        />
        {bg && (
          <Image
            ref={bgImageRef}
            image={bg}
            x={0}
            y={0}
            width={CANVAS_WIDTH}
            height={CANVAS_HEIGHT}
            listening={true}
            onClick={(e: KonvaEventObject<MouseEvent>) => {
              e.cancelBubble = true;
              setSelectedId('canvas');
              if (trRef.current) {
                trRef.current.nodes([]);
                trRef.current.getLayer()?.batchDraw();
              }
            }}
            id="canvas-background-image"
          />
        )}
      </Layer>

      <Layer>
        {logo && (
          <Image
            id="logo"
            ref={logoRef}
            image={logo}
            x={logoProps.x}
            y={logoProps.y}
            width={logoProps.width}
            height={logoProps.height}
            draggable={!previewMode}
            onClick={handleLogoClick}
            onDblClick={!previewMode ? handleLogoDoubleClick : undefined}
            dragBoundFunc={(pos) => limitDrag(pos, logoProps.width, logoProps.height)}
            onDragStart={handleDragStart}
            onDragEnd={handleLogoDragEnd}
            onTransformStart={handleDragStart}
            onTransformEnd={handleDirectLogoTransform}
          />
        )}

        {renderTexts}
        {renderImages}
        <Transformer
          ref={trRef}
          boundBoxFunc={(oldBox, newBox) => {
            const minSize = 10;
            if (newBox.width < minSize || newBox.height < minSize) {
              return oldBox;
            }

            const minVisiblePortion = 20;
            const maxWidth = CANVAS_WIDTH * 2;
            const maxHeight = CANVAS_HEIGHT * 2;
            const rightEdge = newBox.x + newBox.width;
            const bottomEdge = newBox.y + newBox.height;

            if (newBox.width > maxWidth) {
              if (newBox.x !== oldBox.x) {
                newBox.x = rightEdge - maxWidth;
              }
              newBox.width = maxWidth;
            }

            if (newBox.height > maxHeight) {
              if (newBox.y !== oldBox.y) {
                newBox.y = bottomEdge - maxHeight;
              }
              newBox.height = maxHeight;
            }

            if (rightEdge < minVisiblePortion) {
              const requiredWidth = minVisiblePortion - newBox.x;
              if (requiredWidth > 0) {
                newBox.width = requiredWidth;
              }
            }

            if (newBox.x > CANVAS_WIDTH - minVisiblePortion) {
              if (newBox.x === oldBox.x) {
                const maxAllowedWidth = CANVAS_WIDTH - newBox.x + minVisiblePortion;
                if (newBox.width > maxAllowedWidth && maxAllowedWidth > minSize) {
                  newBox.width = maxAllowedWidth;
                }
              } else {
                const maxX = CANVAS_WIDTH - minVisiblePortion;
                if (newBox.x > maxX) {
                  const diff = newBox.x - maxX;
                  newBox.x = maxX;
                  newBox.width += diff;
                }
              }
            }

            if (bottomEdge < minVisiblePortion) {
              const requiredHeight = minVisiblePortion - newBox.y;
              if (requiredHeight > 0) {
                newBox.height = requiredHeight;
              }
            }

            if (newBox.y > CANVAS_HEIGHT - minVisiblePortion) {
              if (newBox.y === oldBox.y) {
                const maxAllowedHeight = CANVAS_HEIGHT - newBox.y + minVisiblePortion;
                if (newBox.height > maxAllowedHeight && maxAllowedHeight > minSize) {
                  newBox.height = maxAllowedHeight;
                }
              } else {
                const maxY = CANVAS_HEIGHT - minVisiblePortion;
                if (newBox.y > maxY) {
                  const diff = newBox.y - maxY;
                  newBox.y = maxY;
                  newBox.height += diff;
                }
              }
            }

            return newBox;
          }}
        />
      </Layer>

      <Layer>
        {selectionRect.visible && (
          <Rect
            x={selectionRectProps?.x || 0}
            y={selectionRectProps?.y || 0}
            width={selectionRectProps?.width || 0}
            height={selectionRectProps?.height || 0}
            fill="rgba(53, 153, 255, 0.3)"
            stroke="rgba(0, 120, 255, 1)"
            strokeWidth={1.5}
            dash={[4, 4]}
            listening={false}
          />
        )}
      </Layer>
    </Stage>
  );
};

const FittedCanvasStage: React.FC<CanvasStageProps> = (props: CanvasStageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  const updateScale = useCallback(() => {
    if (!containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth;
    const containerHeight = containerRef.current.clientHeight;

    const paddingX = 40;
    const paddingY = 40;

    const availableWidth = containerWidth - paddingX;
    const availableHeight = containerHeight - paddingY;

    const scaleX = availableWidth / CANVAS_WIDTH;
    const scaleY = availableHeight / CANVAS_HEIGHT;

    const newScale = Math.min(scaleX, scaleY, 1);

    setScale(newScale);
  }, []);

  useEffect(() => {
    updateScale();

    const resizeObserver = new ResizeObserver(() => {
      updateScale();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateScale]);

  return (
    <div
      ref={containerRef}
      className="canvas-stage-container"
      style={{
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          transition: 'transform 0.2s ease',
        }}
      >
        <CanvasStage {...props} />
      </div>
    </div>
  );
};

export default FittedCanvasStage;
